import {
  Actions,
  Card,
  Empty,
  Flex,
  Heading,
  MessageType,
  toast,
  Tooltip,
} from '@oliasoft-open-source/react-ui-library';
import { withUnit } from '@oliasoft-open-source/units';
import { getExcelObject } from 'client/api-utils';
import translations from 'client/internationalization/translation-map.json';
import { getHierarchyDataStateGrouped } from 'client/store/entities/hierarchy/selectors';
import { upsert, upsertAssociatedData } from 'client/store/entities/object/object';
import { getAssociatedObject, getObjectsByTypeAndParent, getSurveyRunLinkedObjectIds, getSurveyRunsLinkedItemsMap } from 'client/store/entities/object/selectors';
import { RootState } from 'client/store/configureStore';
import { EntityType } from 'common/enums/entity-enums';
import { HierarchyLevelType, IObject } from 'common/interfaces/hierarchy-entities';
import { ICommonTable } from 'common/interfaces/objects-interfaces';
import { getIsLinked } from 'common/utils/is-linkable-table';
import { isVerticalTable } from 'common/utils/is-vertical-table';
import { isArray, isEmpty } from 'es-toolkit/compat';
import {
  Dispatch, SetStateAction, useContext, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FiDownload, FiUpload } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import {
  entityTableLabelMap,
  isVectorTable,
  linkableEntityTablesMap,
  parsePlainCSVText,
} from 'common/table-viewer/utils';
import { getData } from 'common/table-viewer/get-table-data';
import { DateService } from 'common/services/date-service';
import { KnowEdmTableData } from 'common/services/configs/trajectories/table-enums';
import { hasAssociatedData } from 'common/utils/is-multiple-tables';
import { ability, ActionMap } from '@oliasoft/authorization-ui';
import { caslEnums } from 'common/enums/casl-enums';
import { downloadTableArray, formatObjectLikeDataToSimpleFormatFunctor, parseText } from '../utils/export';
import { DataTable, TChangeOptionsPayload } from './data-table';
import { InfoTable } from './info-table';
import { UnitContext } from 'client/views/hoc/with-unit-template';
import { ModalType } from 'common/enums/information-enums';
interface TableViewerProps {
  dataObject: IObject;
  setSelectedData: Dispatch<SetStateAction<IObject | null>>;
  withPaste?: boolean;
  onHeaderClick?: () => void;
  visibleColumns?: string[];
  setSelectedMultipleLinkedItems?: (data: IObject[], modalType: ModalType) => void;
  dataToCompare?: { [k: string]: any };
}

// entities referenced by survey run associated data; their rows are scoped per survey run
// via externalid (the surveyrunsassociateddataid of the survey run that links them).
export const surveyRunLinkedEntityTypes = [
  EntityType.HoleSize,
  EntityType.GeomagneticParameters,
  EntityType.CovarianceMatrix,
  EntityType.SurveyProgram,
];

const surveyRunAssociatedDataLinkPropertyMap = {
  [EntityType.HoleSize]: 'holeSize',
  [EntityType.CovarianceMatrix]: 'covarianceMatrix',
  [EntityType.GeomagneticParameters]: 'geomagneticParametersDefinition'
}

type SurveyRunLinkedEntityType = keyof typeof surveyRunAssociatedDataLinkPropertyMap;

export const TableViewer = ({
  dataObject,
  setSelectedData,
  withPaste,
  onHeaderClick,
  visibleColumns = [],
  setSelectedMultipleLinkedItems,
  dataToCompare,
}: TableViewerProps) => {
  const unitTemplate = useContext(UnitContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [showVectorsCollapsed, setShowVectorsCollapsed] = useState(false);
  const associatedData = useSelector(state => getAssociatedObject(state, dataObject.objectid));

  const designid = dataObject?.metadata?.hierarchyData?.design?.id;
  const isSurveyRunLinkedEntity = surveyRunLinkedEntityTypes.includes(dataObject.type as EntityType);
  const surveyRunLinkedObjectIds = useSelector(
    (state: RootState) => getSurveyRunLinkedObjectIds(state, designid ?? null),
  );
  const surveyRuns = useSelector((state: RootState) => getObjectsByTypeAndParent(state, EntityType.SurveyRun, HierarchyLevelType.Design, designid));
  const surveyRunsLinkedItemsMap = useSelector((state: RootState) => getSurveyRunsLinkedItemsMap(state, designid, dataObject.objectid));
  const linkedSurveyRunAssociatedDataId = surveyRunLinkedObjectIds[dataObject.objectid] ?? null;
  const { isLinked } = getIsLinked(dataObject);
  const hierarchyDataStateGrouped = useSelector(getHierarchyDataStateGrouped);

  const onClickDownLoad = (name: string) => {
    downloadTableArray(getData(dataObject, unitTemplate, false, visibleColumns) as ICommonTable, name || dataObject.type || '', isVerticalTable(dataObject.type as EntityType));
  };

  const onClickDownLoadExcel = () => {
    const isVertical = isVerticalTable(dataObject.type as EntityType);
    const exportData = getData(dataObject, unitTemplate, false, visibleColumns) as ICommonTable;
    const formattedTable = [exportData.headers, ...exportData.rows.map(formatObjectLikeDataToSimpleFormatFunctor)];
    if (isVertical) {
      formattedTable.push(exportData.units);
    } else {
      formattedTable.splice(1, 0, exportData.units);
    }
    getExcelObject(dataObject.objectid, isVertical, formattedTable);
  };

  const onCopyToClipboard = async () => {
    const text = parseText(getData(dataObject, unitTemplate, false, visibleColumns) as ICommonTable, isVerticalTable(dataObject.type as EntityType));
    try {
      await navigator.clipboard.writeText(text);
      toast({
        message: {
          type: MessageType.SUCCESS,
          content: t(translations.copiedToClipboard),
        },
      });
    } catch (error: any) {
      toast({
        message: {
          type: MessageType.ERROR,
          content: t(translations.unableToCopyToClipboard),
          details: error,
        },
      });
    }
  };

  const handlePasteFromClipboard = (text: string) => {
    try {
      if (isSurveyRunLinkedEntity && !linkedSurveyRunAssociatedDataId) {
        toast({
          message: {
            type: MessageType.INFO,
            icon: true,
            content: 'Info',
            details: 'This table is not linked to any survey run associated data. Link it to a survey run before pasting data.',
          },
        });
        return;
      }
      const { headers, units } = getData(dataObject, unitTemplate);
      const pastedData = parsePlainCSVText(text, headers, units, (dataObject.type as EntityType) || null);
      let updateObject = dataObject;
      const dataIsNestedArray = isArray(pastedData.data) && isArray(pastedData.data[0]);
      if (dataIsNestedArray) {
        const [headers, units, ...data] = pastedData.data as string[][];
        updateObject = {
          ...updateObject,
          data: {
            headers, units, rows: data,
          },
        };
      } else {
        updateObject = {
          ...updateObject,
          data: pastedData.data,
        };
      }
      // TODO: move setSelectedData logic to redux
      // needs a bit of refactoring since set selected is used not only for object
      const objectWithUtcData = {
        ...updateObject,
        associatedData: associatedData,
        ...(isSurveyRunLinkedEntity ? { externalid: linkedSurveyRunAssociatedDataId } : {}),
        data: DateService.utcFromLocalTimeByKeys(updateObject.data, [KnowEdmTableData.SurveyDate]),
      };
      dispatch(upsert(objectWithUtcData, setSelectedData));

      const isSurveyRun = dataObject.type === EntityType.SurveyRun;
      if (isSurveyRun && Array.isArray(pastedData.data) && designid && associatedData) {
        const [unitsRow, topRow] = pastedData.data as Record<string, string>[];
        if (topRow) {
          dispatch(upsertAssociatedData(
            dataObject.objectid,
            dataObject.nodeid,
            {
              ...associatedData,
              [KnowEdmTableData.DepthMD]: withUnit(topRow.depthMD ?? 0, unitsRow.depthMD),
              [KnowEdmTableData.Inclination]: withUnit(topRow.inclination ?? 0, unitsRow.inclination),
              [KnowEdmTableData.Azimuth]: withUnit(topRow.azimuth ?? 0, unitsRow.azimuth),
              [KnowEdmTableData.DepthTVD]: withUnit(topRow.depthTVD ?? 0, unitsRow.depthTVD),
              [KnowEdmTableData.LocalPositionEW]: withUnit(topRow.localPositionEW ?? 0, unitsRow.localPositionEW),
              [KnowEdmTableData.LocalPositionNS]: withUnit(topRow.localPositionNS ?? 0, unitsRow.localPositionNS),
            },
            EntityType.SurveyRun,
            designid
          ));
        }
      }
    } catch (error: any) {
      toast({
        message: {
          type: 'Error',
          icon: true,
          content: 'error',
          details: error.message,
        },
      });
    }
  };

  // send request on every change now later change to edittable table,
  // should be handled as ui ticket for some sort
  const handleUpdateOptions = (config: TChangeOptionsPayload) => {
    const {
      dataIndex, value, name, units,
    } = config;
    try {
      // @ts-ignore
      const copy = dataObject.data.map((item, idx) => {
        if (idx === 0) {
          return units;
        }
        return idx === dataIndex ? { ...item, [name]: value } : item;
      });

      const obj = {
        ...dataObject,
        ...(isSurveyRunLinkedEntity ? { externalid: linkedSurveyRunAssociatedDataId } : {}),
        data: copy,
      };
      dispatch(upsert(obj, setSelectedData));
    } catch (error: any) {
      toast({
        message: {
          type: 'Error',
          icon: true,
          content: 'error',
          details: error.message,
        },
      });
    }
  };

  const handleLinkedObjectClick = (value: string | undefined, modalType: ModalType) => {
    const entityType = dataObject.type as EntityType;
    if (!entityType) {
      return;
    }

    if (entityType === EntityType.SurveyProgram && value) {
      const found = surveyRuns.find(i => i.associatedobjectid === value);
      if (found) {
        if (modalType === ModalType.AssociatedData) {
          setSelectedMultipleLinkedItems?.([found], ModalType.AssociatedData);
        } else {
          setSelectedData(found);
        }
      }
      return;
    }

    const linkedEntityType = entityType as SurveyRunLinkedEntityType;
    const linkedItems = surveyRunsLinkedItemsMap[linkedEntityType];
    if (linkedItems.length === 0) {
      toast({
        message: {
          type: MessageType.INFO,
          content: t(translations.object_notLinkedToAny),
        },
      });
      return;
    }

    if (linkedItems.length > 1) {
      toast({
        message: {
          type: MessageType.INFO,
          content: t(translations.objects_linkedToMultiple),
        },
      });

      const linkedSurveyRuns = surveyRuns.filter(i => linkedItems.includes(i.objectid));
      setSelectedMultipleLinkedItems?.(linkedSurveyRuns, modalType);
      return;
    }

    const found = surveyRuns.find(i => i.objectid === linkedItems[0]);
    if (found) {
      if (modalType === ModalType.AssociatedData) {
        setSelectedMultipleLinkedItems?.([found], ModalType.AssociatedData);
      } else {
        setSelectedData(found);
      }
    }
  };

  useEffect(() => {
    const pasteFn = (event: ClipboardEvent) => {
      const data = event.clipboardData?.items;
      if (!data) return;
      const item = Array.from(data).find(item => item.kind === 'string' && item.type.match('^text/plain'));
      item?.getAsString(
        text => handlePasteFromClipboard(text),
      );
    };
    document.addEventListener('paste', pasteFn); // register paste event (Ctrl+V)
    return () => {
      document.removeEventListener('paste', pasteFn); // clean up
    };
  }, []);

  const onPasteButtonClick = async () => {
    if (hasAssociatedData(dataObject?.type as EntityType) && !associatedData) {
      toast({
        message: {
          type: MessageType.INFO,
          icon: true,
          content: 'Info',
          details: 'Please fill associated data first',
        },
      });
      return;
    }
    if (!navigator.clipboard.readText) {
      toast({
        message: {
          type: MessageType.ERROR,
          icon: true,
          content: 'error',
          details: t(translations.importExport_FirefoxPasteError),
        },
      });
      return;
    }
    const text = await navigator.clipboard.readText();
    handlePasteFromClipboard(text);
  };

  const exportDataAction = {
    icon: <Tooltip text={t(translations.importExport_export)}><FiUpload /></Tooltip>,
    subActions: [
      {
        label: `${t(translations.importExport_download)} CSV`,
        onClick: () => onClickDownLoad(
          dataObject.name,
        ),
      },

      {
        label: `${t(translations.importExport_download)} EXCEL`,
        onClick: onClickDownLoadExcel,
      },
      {
        label: `${t(translations.copiedToClipboard)}`,
        onClick: onCopyToClipboard,
      },
    ],
  };

  const importDataAction = {
    icon: <Tooltip text={t(translations.importExport_import)}><FiDownload /></Tooltip>,
    subActions: [
      {
        label: t(translations.importExport_quickPasteFromClipboard),
        onClick: onPasteButtonClick,
      },
    ],
  };

  const tableSettingsAction = {
    icon: 'settings',
    subActions: [
      {
        label: showVectorsCollapsed ? `${t(translations.expandVectorColumns)}` : `${t(translations.collapseVectorColumns)}`,
        tooltip: t(translations.importExport_paste),
        onClick: () => setShowVectorsCollapsed(!showVectorsCollapsed),
      },
    ],
  };

  const hasImportPermission = withPaste && !isLinked && ability.can(ActionMap.MANAGE, caslEnums.structuredDataEntities);
  const isVectorTableType = isVectorTable(dataObject.type as EntityType);

  const actions: any[] = [];

  if (hasImportPermission) {
    actions.push(importDataAction);
  }

  actions.push(exportDataAction);

  if (isVectorTableType) {
    actions.push(tableSettingsAction);
  }

  const getLinkedObject = (object: IObject) => {
    if (!object.metadata.hierarchyData) {
      console.error('Outdated object no hierarchy data present');
      return object;
    }
    const siteid = object.metadata.hierarchyData.site.id;
    const wellid = object.metadata.hierarchyData.well.id;
    const fieldid = object.metadata.hierarchyData.field.id;
    const site = hierarchyDataStateGrouped.sites?.[siteid]?.[0];
    const well = hierarchyDataStateGrouped?.wells?.[wellid]?.[0];
    const field = hierarchyDataStateGrouped?.fields?.[fieldid]?.[0];

    const linkedObject = {
      ...object,
      data: [
        {},
        linkableEntityTablesMap(object.type as EntityType, { site, well, field }),
      ],
    };
    return linkedObject;
  };

  return (
    <Card
      heading={(
        <>
          <Heading onClick={onHeaderClick}>
            {entityTableLabelMap?.[dataObject.name as EntityType] || dataObject.name}
          </Heading>

          <Actions
            actions={actions}
          />
        </>
      )}
      padding={false}
    >
      {isVerticalTable(dataObject.type as EntityType)
        ? <InfoTable
          key={`${dataObject.objectid}`}
          dataObject={isLinked ? getLinkedObject(dataObject) : dataObject}
          t={t}
          unitTemplate={unitTemplate}
          handleLinkedObjectClick={handleLinkedObjectClick}
          dataToCompare={dataToCompare}
        />
        : <DataTable
          dataObject={dataObject}
          visibleColumns={visibleColumns}
          t={t}
          hasVectors={showVectorsCollapsed}
          handleUpdateOptions={handleUpdateOptions}
          unitTemplate={unitTemplate}
          handleLinkedObjectClick={handleLinkedObjectClick}
          dataToCompare={dataToCompare}
        />
      }
      {isEmpty(getData(dataObject).rows)
        && (
          <Flex height={100} justifyContent="center" alignItems="center">
            <Empty text={t(translations.viewer_noData)} />
          </Flex>
        )}
    </Card>
  );
};
